import { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { api } from '@/util/api'
import { usePodStatusWebSocket, useStreamsWebSocket } from '@/hooks/useWebSocket'
import { AdminLayout } from '@/components/admin/AdminLayout'
import {
  Badge,
  Button,
  Card,
  Collapsible,
  ConfirmDialog,
  EmptyState,
  Meter,
  Notice,
  Select,
  Skeleton,
  SkeletonGroup,
  ToggleGroup,
  type BadgeTone,
} from '@/components/play'
import { Activity, Radio, Server, Video, TriangleAlert, Eraser, Building2 } from 'lucide-react'
import { groupStreamsByOrgAndEvent, type GroupedStreams } from '@/lib/admin/healthGrouping'
import type { InfrastructureHealth, StreamHealth } from '@/types/admin'

function timeAgo(iso: string | null): string {
  if (!iso) return 'never'
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (seconds < 60) return `${seconds}s ago`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

function formatUptime(seconds: number): string {
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`
  return `${Math.floor(seconds / 86400)}d`
}

/** Server states arrive in lower case; badges use sentence case. */
function sentenceCase(text: string) {
  return text ? text.charAt(0).toUpperCase() + text.slice(1).replace(/_/g, ' ') : text
}

/** A stream's lifecycle. Active takes the accent tone, as on the streams monitor: it isn't proof that video is
 *  arriving, and success green is kept for live media evidence. */
function statusBadgeTone(status: string): BadgeTone {
  switch (status) {
    case 'active': return 'accent'
    case 'warm': return 'neutral'
    case 'provisioning': return 'neutral'
    case 'terminated': return 'error'
    case 'error': return 'error'
    default: return 'quiet'
  }
}

// Route heartbeats are refreshed by PodHealthCheckJob every 60s.
// Use a 90s threshold (interval + buffer).
const ROUTE_HEALTHY_THRESHOLD_MS = 90_000

type HealthChip = { text: string; tone: BadgeTone }

function routeChip(stream: StreamHealth): HealthChip {
  if (!stream.route_status) return { text: 'SRT --', tone: 'quiet' }
  if (stream.route_status !== 'active') {
    return { text: `SRT ${stream.route_status}`, tone: 'error' }
  }
  if (stream.route_last_heartbeat) {
    const ageMs = Date.now() - new Date(stream.route_last_heartbeat).getTime()
    if (ageMs < ROUTE_HEALTHY_THRESHOLD_MS) {
      return { text: 'SRT ok', tone: 'success' }
    }
    return { text: `SRT stale ${timeAgo(stream.route_last_heartbeat)}`, tone: 'error' }
  }
  return { text: 'SRT stale', tone: 'error' }
}

function liveKitChip(stream: StreamHealth): HealthChip {
  if (!stream.livekit_expected) return { text: 'RTC --', tone: 'quiet' }
  if (stream.livekit_healthy === true) return { text: 'RTC ok', tone: 'success' }
  if (stream.livekit_healthy === false) return { text: 'RTC off', tone: 'error' }
  if (stream.webrtc_status === 'connected') return { text: 'RTC ok', tone: 'success' }
  if (stream.webrtc_status === 'disconnected') return { text: 'RTC off', tone: 'error' }
  return { text: 'RTC pending', tone: 'warning' }
}

function podHealthTone(lastCheck: string | null): BadgeTone {
  if (!lastCheck) return 'quiet'
  const seconds = (Date.now() - new Date(lastCheck).getTime()) / 1000
  if (seconds < 60) return 'success'
  if (seconds < 180) return 'warning'
  return 'error'
}

function unhealthyReason(stream: StreamHealth): string {
  if (!stream.pod) return 'No pod — provisioning failed'
  if (!stream.pod.healthy) return 'Pod unhealthy'
  if (!stream.route_healthy) return 'SRT route stale'
  if (stream.livekit_expected && stream.livekit_healthy === false) return 'LiveKit publisher offline'
  return 'Degraded'
}

export function AdminHealthDashboardPage() {
  const [data, setData] = useState<InfrastructureHealth | null>(null)
  const [loading, setLoading] = useState(true)
  const [orgFilter, setOrgFilter] = useState<string>('all')
  const [eventFilter, setEventFilter] = useState<string>('all')
  const [showUnhealthyOnly, setShowUnhealthyOnly] = useState(false)
  // The last request failed: with no data the page says so; with data it says the figures are the last ones loaded.
  const [refreshFailed, setRefreshFailed] = useState(false)

  const loadHealth = useCallback(async () => {
    try {
      const eventId = eventFilter !== 'all' ? eventFilter : undefined
      const organizationId = orgFilter !== 'all' ? orgFilter : undefined
      const res = await api.getAdminHealth(eventId, organizationId)
      setData(res.data)
      setRefreshFailed(false)
    } catch {
      setRefreshFailed(true)
    } finally {
      setLoading(false)
    }
  }, [eventFilter, orgFilter])

  useEffect(() => {
    loadHealth()
    const interval = setInterval(loadHealth, 10000)
    return () => clearInterval(interval)
  }, [loadHealth])

  const refreshTimer = useRef<ReturnType<typeof setTimeout>>()
  const debouncedRefresh = useCallback(() => {
    clearTimeout(refreshTimer.current)
    refreshTimer.current = setTimeout(loadHealth, 300)
  }, [loadHealth])

  // Pod status changes → full refresh
  usePodStatusWebSocket(useCallback(() => {
    debouncedRefresh()
  }, [debouncedRefresh]))

  // Real-time stream status patches + full refresh
  const handleStreamUpsert = useCallback((streamData: any) => {
    const attrs = streamData?.data?.attributes
    if (!attrs) return
    setData((prev) => {
      if (!prev) return prev
      const updated = prev.streams.map((s) => {
        if (s.id !== attrs.id) return s
        return { ...s, status: attrs.status, webrtc_status: attrs.webrtc_status ?? s.webrtc_status }
      })
      return { ...prev, streams: updated.filter((s) => s.status !== 'stopped') }
    })
    debouncedRefresh()
  }, [debouncedRefresh])

  const handleStreamRemove = useCallback((streamId: string) => {
    setData((prev) => {
      if (!prev) return prev
      return { ...prev, streams: prev.streams.filter((s) => s.id !== streamId) }
    })
    debouncedRefresh()
  }, [debouncedRefresh])

  useStreamsWebSocket(handleStreamUpsert, handleStreamRemove)

  // Cascade: filter events list based on selected org
  const filteredEvents = useMemo(() => {
    if (!data) return []
    if (orgFilter === 'all') return data.events
    return data.events.filter((e) => e.organization_id === orgFilter)
  }, [data, orgFilter])

  // Reset event filter when org changes and the selected event isn't in the new org
  useEffect(() => {
    if (eventFilter === 'all') return
    const stillValid = filteredEvents.some((e) => e.id === eventFilter)
    if (!stillValid) setEventFilter('all')
  }, [filteredEvents, eventFilter])

  // Group visible streams
  const visibleStreams = useMemo(() => {
    if (!data) return []
    return showUnhealthyOnly ? data.streams.filter((s) => s.unhealthy) : data.streams
  }, [data, showUnhealthyOnly])

  const grouped = useMemo(
    () => groupStreamsByOrgAndEvent(visibleStreams, data?.events ?? []),
    [data?.events, visibleStreams]
  )

  async function handleCleanup() {
    await api.cleanupAdminPods(false)
    loadHealth()
  }

  const heading = <h1 className="text-3xl font-semibold">Infrastructure</h1>

  if (loading) {
    return (
      <AdminLayout>
        <div className="space-y-6">
          {heading}
          <SkeletonGroup label="Loading infrastructure health" className="grid gap-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} shape="panel" className="h-24" />
              ))}
            </div>
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} shape="panel" className="h-28" />
              ))}
            </div>
          </SkeletonGroup>
        </div>
      </AdminLayout>
    )
  }

  const retry = <Button variant="secondary" size="compact" onClick={() => void loadHealth()}>Try again</Button>

  if (!data) {
    return (
      <AdminLayout>
        <div className="space-y-6">
          {heading}
          <Notice tone="error" action={retry}>We couldn’t load infrastructure health.</Notice>
        </div>
      </AdminLayout>
    )
  }

  const { srt_router, livekit, streams, organizations, summary } = data
  const unhealthyCount = streams.filter((s) => s.unhealthy).length

  return (
    <AdminLayout>
      <div className="space-y-6">
        {heading}

        {refreshFailed && (
          <Notice tone="warning" action={retry}>Couldn’t refresh. These are the last figures loaded.</Notice>
        )}

        {/* Summary bar */}
        <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <Card as="li" title="SRT router" icon={<Radio />}>
            <Badge tone={srt_router.healthy ? 'success' : 'error'}>{srt_router.healthy ? 'Online' : 'Offline'}</Badge>
            <p className="mt-2 text-xs text-neutral-7">
              DB {srt_router.db_connected ? 'connected' : 'disconnected'}
            </p>
          </Card>

          <Card as="li" title="Streams" icon={<Video />}>
            <div className="text-2xl font-bold">{summary.total_active_streams}</div>
            <p className="text-xs text-neutral-7">active</p>
          </Card>

          <Card as="li" title="Pods" icon={<Server />}>
            <div className="text-2xl font-bold">{summary.total_active_pods}</div>
            <p className="text-xs text-neutral-7">running</p>
          </Card>

          <Card as="li" title="LiveKit" icon={<Radio />}>
            <Badge tone={livekit.healthy ? 'success' : 'error'}>{livekit.healthy ? 'Online' : 'Offline'}</Badge>
            <p className="mt-2 text-xs text-neutral-7">
              {livekit.configured ? `${livekit.connected_streams ?? 0}/${livekit.active_streams_expected ?? 0} connected` : 'not configured'}
            </p>
            {livekit.stale_ingress_count ? (
              <p className="text-xs text-error">
                {livekit.stale_ingress_count} stale ingress{livekit.stale_ingress_count === 1 ? '' : 'es'}
              </p>
            ) : (
              <p className="text-xs text-neutral-7">
                {livekit.ingress_count ?? 0} ingress{livekit.ingress_count === 1 ? '' : 'es'}
              </p>
            )}
          </Card>

          <Card as="li" title="Unhealthy" icon={<TriangleAlert />}>
            <div className={unhealthyCount > 0 ? 'text-2xl font-bold text-error' : 'text-2xl font-bold'}>
              {unhealthyCount}
            </div>
            <p className="text-xs text-neutral-7">
              {unhealthyCount === 0 ? 'all healthy' : `of ${streams.filter(s => s.status === 'active').length} active`}
            </p>
          </Card>
        </ul>

        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3">
          <Select
            className="w-[224px]"
            label="Organization"
            icon={<Building2 />}
            items={[
              { value: 'all', label: 'All organizations' },
              ...organizations.map((o) => ({ value: o.id, label: o.name })),
            ]}
            value={orgFilter}
            onValueChange={(v) => setOrgFilter(v ?? 'all')}
          />

          <Select
            className="w-[200px]"
            label="Event"
            items={[
              { value: 'all', label: 'All events' },
              ...filteredEvents.map((e) => ({ value: e.id, label: e.name })),
            ]}
            value={eventFilter}
            onValueChange={(v) => setEventFilter(v ?? 'all')}
          />

          <ToggleGroup
            label="Show"
            items={[
              { value: 'all', label: 'All' },
              // Kept choosable while chosen, so the filter can't strand itself when the last unhealthy stream recovers.
              { value: 'unhealthy', label: 'Unhealthy', icon: <TriangleAlert />, disabled: unhealthyCount === 0 && !showUnhealthyOnly },
            ]}
            value={showUnhealthyOnly ? 'unhealthy' : 'all'}
            onValueChange={(value) => setShowUnhealthyOnly(value === 'unhealthy')}
          />

          <div className="ml-auto">
            <ConfirmDialog
              trigger={<Button variant="secondary" size="compact"><Eraser />Clean up</Button>}
              title="Clean up pods?"
              description="Removes terminated pods older than 24 hours and pods with no backing infrastructure, and stops pods with no recent health check."
              confirmLabel="Clean up pods"
              onConfirm={handleCleanup}
            />
          </div>
        </div>

        {/* Grouped stream list */}
        {visibleStreams.length === 0 ? (
          <EmptyState
            icon={<Video />}
            title={streams.length === 0 ? 'No active streams' : 'No streams match these filters'}
          />
        ) : (
          <div className="space-y-4">
            {grouped.map((org) => (
              <OrgSection key={org.orgName} org={org} singleOrg={grouped.length === 1} />
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  )
}

function OrgSection({
  org,
  singleOrg,
}: {
  org: GroupedStreams[number]
  singleOrg: boolean
}) {
  const [open, setOpen] = useState(true)
  const totalStreams = org.events.reduce((sum, e) => sum + e.streams.length, 0)
  const unhealthyCount = org.events.reduce(
    (sum, e) => sum + e.streams.filter((s) => s.unhealthy).length,
    0
  )

  // If only one org (filtered), skip the collapsible wrapper
  if (singleOrg) {
    return (
      <div className="space-y-3">
        {org.events.map((event) => (
          <EventSection key={event.eventId ?? '_none'} event={event} singleEvent={org.events.length === 1} />
        ))}
      </div>
    )
  }

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      trigger={
        <>
          <Building2 />
          <span>{org.orgName}</span>
          <span className="text-xs font-normal text-neutral-7">
            {totalStreams} stream{totalStreams !== 1 ? 's' : ''}
          </span>
          {unhealthyCount > 0 && (
            <Badge tone="error">{unhealthyCount} unhealthy</Badge>
          )}
        </>
      }
    >
      {org.events.map((event) => (
        <EventSection key={event.eventId ?? '_none'} event={event} singleEvent={org.events.length === 1} />
      ))}
    </Collapsible>
  )
}

function EventSection({
  event,
  singleEvent,
}: {
  event: GroupedStreams[number]['events'][number]
  singleEvent: boolean
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 px-1">
        {/* If only one event in the org, show its streams without the event icon */}
        {!singleEvent && <Video size={14} className="text-neutral-7" aria-hidden="true" />}
        <span className="text-xs font-medium text-neutral-7">{event.eventName}</span>
        <span className="text-xs text-neutral-7">{event.streams.length} stream{event.streams.length !== 1 ? 's' : ''}</span>
      </div>
      <ul className="divide-y divide-neutral-4">
        {event.streams.map((stream) => (
          <StreamRow key={stream.id} stream={stream} />
        ))}
      </ul>
    </div>
  )
}

function StreamRow({
  stream,
}: {
  stream: StreamHealth
}) {
  const route = routeChip(stream)
  const liveKit = liveKitChip(stream)

  return (
    <li className="min-w-0 space-y-1.5 px-1 py-3">
      <div className="flex items-center gap-2 flex-wrap">
        {stream.unhealthy && (
          <TriangleAlert size={14} className="text-error shrink-0" aria-hidden="true" />
        )}
        <span className="font-semibold truncate">{stream.name}</span>
        <Badge tone={statusBadgeTone(stream.status)}>{sentenceCase(stream.status)}</Badge>
      </div>

      {/* Health chips */}
      <div className="flex items-center gap-2 flex-wrap">
        <Badge tone={route.tone}>{route.text}</Badge>
        <Badge tone={liveKit.tone}>{liveKit.text}</Badge>
        {stream.unhealthy && (
          <Badge tone="error" icon={<TriangleAlert />}>{unhealthyReason(stream)}</Badge>
        )}
      </div>

      {/* Pod info */}
      {stream.pod ? (
        <div className="flex flex-wrap items-center gap-2 text-xs text-neutral-7">
          <Badge tone={podHealthTone(stream.pod.last_health_check)} icon={<Activity />}>
            <span className="sr-only">Health check </span>
            {stream.pod.last_health_check ? timeAgo(stream.pod.last_health_check) : 'No health check'}
          </Badge>
          <span className="font-data truncate max-w-[180px]">{stream.pod.pod_name}</span>
          <span aria-hidden="true">·</span>
          <span>up {formatUptime(stream.pod.uptime_seconds)}</span>
          {stream.pod.memory_usage != null && (
            <>
              <span aria-hidden="true">·</span>
              <Meter inline className="w-40" label="Memory" value={stream.pod.memory_usage} high={85} />
            </>
          )}
        </div>
      ) : stream.status === 'active' ? (
        <p className="text-xs text-error">No pod — stream is running without infrastructure</p>
      ) : (
        <p className="text-xs text-neutral-7">Provisioning…</p>
      )}
    </li>
  )
}
